import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  EarningsCalendarDay,
  EarningsCalendarEvent,
  EarningsCalendarResponse,
} from "../../../hooks/useEarningsCalendar";

dayjs.extend(utc);

// Pure selection rules for the earnings week grid. Kept out of the components
// so the editorial decisions — how a day splits, how many rows each side
// shows, what the door hides, how tall everything is — are testable and stated
// in one place.

/** Rows each session column shows before folding the rest into the day's door.
 *  A peak reporting day carries ~200 eligible companies; each side shows the
 *  biggest handful (the server already ordered them) and the door opens the
 *  rest. Both sides get the same allowance so the columns stay symmetrical. */
export const MAX_ROWS_PER_SESSION = 5;

// Fixed geometry, so every day block on the board is the same height whatever
// it holds: rows are sized explicitly rather than by content, the session body
// always reserves MAX_ROWS_PER_SESSION slots, and the footer is always
// present. The skeleton mirrors the same numbers.
export const ROW_HEIGHT = 46;
export const ROW_GAP = 6;
export const SESSION_HEADER_HEIGHT = 16;
export const SESSION_BODY_HEIGHT =
  MAX_ROWS_PER_SESSION * ROW_HEIGHT + (MAX_ROWS_PER_SESSION - 1) * ROW_GAP;
export const FOOTER_HEIGHT = 32;

export type EarningsSessionKey = "bmo" | "amc";

export interface EarningsSession {
  key: EarningsSessionKey;
  label: string;
  events: EarningsCalendarEvent[];
}

// Amber for the open, blue for the close — sunrise and evening. Both are house
// palette colours, and the pairing carries through to the day modal.
export const SESSION_COLORS: Record<EarningsSessionKey, string> = {
  bmo: "#f59e0b",
  amc: "#3b82f6",
};

export const SESSION_LABELS: Record<EarningsSessionKey, string> = {
  bmo: "Before open",
  amc: "After close",
};

/** FMP has confirmed a session for this row. False on roughly a third of the
 *  eligible universe — a provider coverage gap, not a fact about the company. */
export const isTimeConfirmed = (event: EarningsCalendarEvent): boolean =>
  event.callTime !== null;

// Which side of the day a row sits on. Confirmed rows go where FMP put them.
// Unconfirmed rows are folded in by exchange rather than dropped (that would
// silently lose names like FedEx Freight and Bunzl) or shown in a third strip
// (which broke the board's symmetry): UK companies release via RNS at 07:00
// London, so an LSE row is before the open; a US row defaults to after the
// close, which at least keeps it on the right day. The card carries a TBC mark
// so the fold never passes for a confirmation.
export const resolveSession = (
  event: EarningsCalendarEvent,
): EarningsSessionKey => {
  if (event.callTime) return event.callTime;
  return event.exchange === "LSE" ? "bmo" : "amc";
};

const ofSession = (
  events: EarningsCalendarEvent[],
  key: EarningsSessionKey,
): EarningsSession => ({
  key,
  label: SESSION_LABELS[key],
  events: events.filter((event) => resolveSession(event) === key),
});

export interface DaySplit {
  /** Always present, always paired — the two sit side by side at equal width
   *  even when one is empty, so the columns line up down the whole week. */
  bmo: EarningsSession;
  amc: EarningsSession;
}

export const splitDay = (events: EarningsCalendarEvent[]): DaySplit => ({
  bmo: ofSession(events, "bmo"),
  amc: ofSession(events, "amc"),
});

export interface VisibleDay {
  date: string;
  bmo: EarningsSession;
  amc: EarningsSession;
  /** Rows withheld across the whole day, reachable through its door. */
  hiddenCount: number;
  totalCount: number;
}

const trim = (session: EarningsSession, max: number): EarningsSession => ({
  ...session,
  events: session.events.slice(0, max),
});

// Trim a day for the week grid. Each session keeps the server's biggest-first
// order, so the largest companies always survive the cut.
export const toVisibleDay = (
  day: EarningsCalendarDay,
  maxPerSession = MAX_ROWS_PER_SESSION,
): VisibleDay => {
  const split = splitDay(day.events);
  const bmo = trim(split.bmo, maxPerSession);
  const amc = trim(split.amc, maxPerSession);
  const shown = bmo.events.length + amc.events.length;

  return {
    date: day.date,
    bmo,
    amc,
    hiddenCount: Math.max(0, day.events.length - shown),
    totalCount: day.events.length,
  };
};

// The board always shows Monday to Friday, so a quiet day reads as "nothing
// scheduled" rather than silently going missing. The server only sends days
// that have rows; the scaffold is built here from the week's Monday. Weekend
// days are appended only when they actually carry entries (foreign issuers,
// and FMP's own date slips).
export const toWeekDays = (
  weekStart: string,
  days: EarningsCalendarDay[],
): EarningsCalendarDay[] => {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const monday = dayjs.utc(weekStart);
  const weekdays = Array.from({ length: 5 }, (_, offset) => {
    const date = monday.add(offset, "day").format("YYYY-MM-DD");
    return byDate.get(date) ?? { date, events: [] };
  });
  const weekdayDates = new Set(weekdays.map((day) => day.date));
  const weekend = days
    .filter((day) => !weekdayDates.has(day.date) && day.events.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  return [...weekdays, ...weekend];
};

// What the board shows for a week. A size floor trims the view client-side —
// the server ships every company over $1B, ordered, so this costs no second
// cache key — compared against the USD-normalised cap so a UK name isn't
// judged in pounds. The free tier's body is trimmed server-side, so
// scaffolding it Monday to Friday would present the stripped days as
// genuinely empty; it keeps just what it was sent and the locked panel
// accounts for the rest.
export const toBoardDays = (
  data: EarningsCalendarResponse,
  minMarketCapUsd: number | null,
): EarningsCalendarDay[] => {
  const days =
    minMarketCapUsd === null
      ? data.days
      : data.days.map((day) => ({
          ...day,
          events: day.events.filter(
            (event) => (event.marketCapUsd ?? 0) >= minMarketCapUsd,
          ),
        }));
  if (data.locked) return days.filter((day) => day.events.length > 0);
  return toWeekDays(data.weekStart, days);
};

/** The board's "today". Reporting dates are calendar dates, so the marker
 *  follows the viewer's own calendar — a New York reader at 20:30 on Monday
 *  is still on Monday whatever the UTC clock says — the same clock the
 *  Watchlist tab beside it buckets its weeks on. */
export const todayKey = (now: Dayjs = dayjs()): string =>
  now.format("YYYY-MM-DD");

// The day's footer is always present so every block is the same height, and
// always opens the day while there is one to open.
export const footerLabel = (day: VisibleDay): string | null => {
  if (day.totalCount === 0) return null;
  if (day.hiddenCount > 0) return `+${day.hiddenCount} more`;
  return "View day";
};
